import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { RadialBars, type RadialDatum } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { formatCashFull } from "utils/formatCash";
import { collapseToTop } from "utils/charts/collapseToTop";
import { foldUnnamedCategories } from "utils/charts/foldUnnamedCategories";
import CardHeader from "components/common/CardHeader";
import CustomSkeleton from "components/common/CustomSkeleton";
import { Center } from "components/layout/reusableLayouts";

/** How many rings the panel draws. Past this they stop being readable. */
const MAX_RINGS = 5;

interface TopCategoriesPanelProps {
  /** Spend per category, in any order. */
  categories: RadialDatum[];
  currency?: string;
  loading?: boolean;
  /** Sum the categories past the last ring into an "Other" ring instead of dropping them. */
  showRemainder?: boolean;
}

/**
 * Where the money went, as concentric rings. The ordering is the point: the
 * outermost ring is the largest category, so the shape reads before the legend
 * does.
 */
const TopCategoriesPanel: React.FC<TopCategoriesPanelProps> = ({
  categories,
  currency,
  loading = false,
  showRemainder = false,
}) => {
  const { t } = useTranslation();

  const named = foldUnnamedCategories(categories);
  const rings = showRemainder
    ? collapseToTop(named, MAX_RINGS, (count) =>
        t("Other ({{count}})", { count }),
      )
    : [...named].sort((a, b) => b.value - a.value).slice(0, MAX_RINGS);

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        height: "100%",
        padding: "var(--space-6)",
        gap: "var(--space-4)",
      }}
    >
      <CardHeader
        title={t("Top categories")}
        subtitle={t("Largest spend areas")}
      />

      {loading && rings.length === 0 ? (
        <CustomSkeleton variant="graph" />
      ) : rings.length === 0 ? (
        <Center sx={{ flex: 1, justifyContent: "center", textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            {t("No Data Found")}
          </Typography>
        </Center>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 240,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RadialBars
            data={rings}
            valueFormatter={(datum) => formatCashFull(datum.value, currency)}
            sx={{ flex: "1 1 0", minHeight: 0 }}
          />
        </Box>
      )}
    </Card>
  );
};

export default TopCategoriesPanel;
